import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import {
  Music2,
  LineChart,
  TrendingUp,
  Headphones,
  CheckCircle2,
  ArrowRight,
  Users,
  Compass,
  Settings2,
  Sparkles,
  Search,
  Wrench,
  Clock3,
  TrendingDown,
} from 'lucide-react';

/**
 * ServicesPage — Hands-on consulting services for radio stations.
 *
 * Layout inspired by the user-provided reference screenshots:
 *  - Hero section (centered headline + sub + primary CTA)
 *  - 4-column service cards with icon + bullet list + per-card CTA
 *  - Process steps (4 circles in a horizontal track)
 *  - Benefits grid (2-column check list)
 *  - Final purple CTA banner
 *
 * Visuals follow the existing dark-purple theme of /about so the page
 * feels native to the rest of radio-apps.online (no light-mode break).
 */

const SERVICE_ICON_MAP = {
  music: Music2,
  market: LineChart,
  growth: TrendingUp,
  support: Headphones,
};

const PROCESS_ICON_MAP = {
  discovery: Search,
  strategy: Compass,
  implementation: Wrench,
  refinement: Sparkles,
};

/**
 * AnimatedCounter — counts from 0 → `to` once it scrolls into view.
 * Uses IntersectionObserver + requestAnimationFrame + ease-out cubic
 * for a slow, smooth count-up effect.
 *
 * Props:
 *   to       (number) final value
 *   rangeTo  (number) optional — if set, renders as "{value}-{rangeValue}"
 *                     (both numbers animate in sync; useful for "5-10%")
 *   duration (ms)     total animation duration (default 2500ms = slow)
 *   suffix   (string) appended after the number (e.g. "%", "+")
 *   prefix   (string) prepended before the number (e.g. "+")
 *   decimals (number) decimal places (default 0)
 */
const AnimatedCounter = ({
  to,
  rangeTo = null,
  duration = 2500,
  suffix = '',
  prefix = '',
  decimals = 0,
  testId,
}) => {
  const [value, setValue] = useState(0);
  const [rangeValue, setRangeValue] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started) {
            setStarted(true);
          }
        });
      },
      { threshold: 0.35 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const start = performance.now();
    const animate = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      // ease-out cubic — slow finish
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(to * eased);
      if (rangeTo !== null) setRangeValue(rangeTo * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        setValue(to);
        if (rangeTo !== null) setRangeValue(rangeTo);
      }
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [started, to, rangeTo, duration]);

  const fmt = (v) => (decimals > 0 ? v.toFixed(decimals) : Math.round(v));
  const display =
    rangeTo !== null ? `${fmt(value)}-${fmt(rangeValue)}` : `${fmt(value)}`;
  return (
    <span ref={ref} data-testid={testId}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

const ServicesPage = () => {
  const { t } = useLanguage();

  useEffect(() => {
    document.title = `${t('servicesTitle') || 'Υπηρεσίες'} | Radio Solutions Apps`;
    window.scrollTo(0, 0);
  }, [t]);

  const services = [
    {
      id: 'music-programming',
      iconKey: 'music',
      title: t('svc1Title') || 'Music Programming & Format Consulting',
      desc:
        t('svc1Desc') ||
        'Βελτιστοποίηση μουσικού προγράμματος με βάση το format, τον ανταγωνισμό και τις προτιμήσεις του κοινού της περιοχής σας.',
      bulletsKey: 'svc1Bullets',
      gradient: 'from-fuchsia-500 to-purple-600',
    },
    {
      id: 'market-analysis',
      iconKey: 'market',
      title: t('svc2Title') || 'Audience & Market Analysis',
      desc:
        t('svc2Desc') ||
        'Αναλύουμε την αγορά και τις μουσικές τάσεις ώστε ο σταθμός να λαμβάνει καλύτερες αποφάσεις.',
      bulletsKey: 'svc2Bullets',
      gradient: 'from-blue-500 to-indigo-600',
    },
    {
      id: 'growth-consulting',
      iconKey: 'growth',
      title: t('svc3Title') || 'Radio Station Growth Consulting',
      desc:
        t('svc3Desc') ||
        'Συμβουλευτική ανάπτυξης για αύξηση ακροαματικότητας, εμπορικής αξίας και αναγνωρισιμότητας.',
      bulletsKey: 'svc3Bullets',
      gradient: 'from-amber-500 to-orange-600',
    },
    {
      id: 'ongoing-support',
      iconKey: 'support',
      title: t('svc4Title') || 'Ongoing Programming Support',
      desc:
        t('svc4Desc') ||
        'Συνεχής συνεργασία και υποστήριξη για σταθμούς που θέλουν εξωτερικό συνεργάτη προγραμματισμού.',
      bulletsKey: 'svc4Bullets',
      gradient: 'from-emerald-500 to-teal-600',
    },
  ];

  // Service bullet lists (fallback in Greek). The translations object
  // returns an array via t() — but our minimal t() returns string, so
  // we keep them inline and let translations.js provide the localized
  // arrays via specific keys (svc1Bullet1 … svcNBulletM).
  const svcBullets = {
    'music-programming': [
      t('svc1Bullet1') || 'Ανάλυση format',
      t('svc1Bullet2') || 'Μουσική στρατηγική',
      t('svc1Bullet3') || 'Διαχείριση rotations',
      t('svc1Bullet4') || 'Competitive analysis',
      t('svc1Bullet5') || 'Δημιουργία μουσικών clocks',
      t('svc1Bullet6') || 'Βελτιστοποίηση rotations',
      t('svc1Bullet7') || 'Category management',
      t('svc1Bullet8') || 'Daypart scheduling',
      t('svc1Bullet9') || 'Weekly music review',
    ],
    'market-analysis': [
      t('svc2Bullet1') || 'Ανάλυση ανταγωνισμού',
      t('svc2Bullet2') || 'Monitoring μουσικών τάσεων',
      t('svc2Bullet3') || 'Αναφορές νέων κυκλοφοριών',
      t('svc2Bullet4') || 'Στρατηγικές διαφοροποίησης',
    ],
    'growth-consulting': [
      t('svc3Bullet1') || 'Branding σταθμού',
      t('svc3Bullet2') || 'Positioning',
      t('svc3Bullet3') || 'Διαγωνισμούς και promotions',
      t('svc3Bullet4') || 'Social media strategy',
      t('svc3Bullet5') || 'Listener engagement',
    ],
    'ongoing-support': [
      t('svc4Bullet1') || 'Εβδομαδιαία επικοινωνία',
      t('svc4Bullet2') || 'Αξιολόγηση μουσικού προγράμματος',
      t('svc4Bullet3') || 'Προτάσεις βελτίωσης',
      t('svc4Bullet4') || 'Υποστήριξη παραγωγών',
      t('svc4Bullet5') || 'Παρακολούθηση αποτελεσμάτων',
    ],
  };

  const processSteps = [
    {
      iconKey: 'discovery',
      title: t('svcProcessStep1Title') || 'Ανακάλυψη',
      desc:
        t('svcProcessStep1Desc') ||
        'Ξεκινάμε κατανοώντας το format, το κοινό και τους στόχους σας μέσα από αναλυτική συζήτηση.',
    },
    {
      iconKey: 'strategy',
      title: t('svcProcessStep2Title') || 'Στρατηγική',
      desc:
        t('svcProcessStep2Desc') ||
        'Σχεδιάζουμε εξατομικευμένη στρατηγική προγραμματισμού προσαρμοσμένη στις ανάγκες σας.',
    },
    {
      iconKey: 'implementation',
      title: t('svcProcessStep3Title') || 'Υλοποίηση',
      desc:
        t('svcProcessStep3Desc') ||
        'Εφαρμόζουμε τη στρατηγική, βελτιστοποιώντας logs, βιβλιοθήκη και clocks για μέγιστη απόδοση.',
    },
    {
      iconKey: 'refinement',
      title: t('svcProcessStep4Title') || 'Βελτίωση',
      desc:
        t('svcProcessStep4Desc') ||
        'Συνεχής ανατροφοδότηση και προσαρμογή ώστε ο ήχος του σταθμού να μένει πάντα βελτιστοποιημένος.',
    },
  ];

  const benefits = [
    t('svcBenefit1') || 'Μεγαλύτερη ακροαματικότητα',
    t('svcBenefit2') || 'Περισσότερος χρόνος παραμονής των ακροατών στη συχνότητα',
    t('svcBenefit3') || 'Αύξηση διαφημιστικής αξίας και εσόδων',
    t('svcBenefit4') || 'Μουσική που κρατά το κοινό συντονισμένο',
    t('svcBenefit5') || 'Λιγότερες αποχωρήσεις ακροατών λόγω κουραστικών επιλογών',
    t('svcBenefit6') || 'Καλύτερες αποφάσεις μέσα από δεδομένα και αναλύσεις',
    t('svcBenefit7') || 'Ξεκάθαρη διαφοροποίηση από τον ανταγωνισμό',
    t('svcBenefit8') || 'Συνεχής βελτίωση του μουσικού προγράμματος',
  ];

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-[#1a0535] via-[#2D0C4B] to-[#1a0535] text-white"
      data-testid="services-page"
    >
      {/* ─────────── HERO ─────────── */}
      <section className="relative pt-28 pb-16 lg:pt-32 lg:pb-20 overflow-hidden">
        {/* Decorative gradient orbs */}
        <div className="absolute top-20 -left-32 w-96 h-96 bg-fuchsia-600/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-40 -right-32 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl pointer-events-none" />

        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <p
              className="inline-block px-4 py-1.5 mb-6 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 text-sm text-purple-200 tracking-wide"
              data-testid="services-hero-eyebrow"
            >
              {t('servicesEyebrow') || 'Υπηρεσίες · Services'}
            </p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-5"
              data-testid="services-hero-title"
            >
              <span className="bg-gradient-to-r from-amber-300 via-fuchsia-300 to-purple-300 bg-clip-text text-transparent">
                {(() => {
                  const title =
                    t('servicesHeroTitle') ||
                    'Music Scheduling & Log Optimization';
                  // Break the title after "&" so "Log Optimization"
                  // wraps onto its own line (user request).
                  const m = title.match(/^(.*?\s*&)\s+(.+)$/);
                  if (m) {
                    return (
                      <>
                        {m[1]}
                        <br />
                        {m[2]}
                      </>
                    );
                  }
                  return title;
                })()}
              </span>
            </h1>
            <p
              className="text-base md:text-lg text-purple-100/85 max-w-3xl mx-auto mb-9 leading-relaxed"
              data-testid="services-hero-desc"
            >
              {t('servicesHeroDesc') ||
                'Πέρα από τα εργαλεία μας, παρέχουμε εξειδικευμένες υπηρεσίες που βοηθούν τους ραδιοφωνικούς σταθμούς να αυξήσουν την ακροαματικότητα, να βελτιώσουν το πρόγραμμά τους και να ενισχύσουν τα έσοδά τους.'}
            </p>
            <Link to="/contact" onClick={() => window.scrollTo(0, 0)}>
              <Button
                size="lg"
                className="bg-gradient-to-r from-fuchsia-500 to-purple-600 hover:from-fuchsia-400 hover:to-purple-500 text-white font-semibold px-8 py-6 text-base shadow-xl hover:shadow-fuchsia-500/40 hover:scale-105 transition-all duration-300"
                data-testid="services-hero-cta"
              >
                {t('servicesCtaFreeEval') || 'Ζητήστε Δωρεάν Αξιολόγηση'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ─────────── 4 SERVICE CARDS ─────────── */}
      <section className="py-16 lg:py-20 bg-black/15">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <h2
              className="text-3xl lg:text-4xl font-bold mb-3"
              data-testid="services-section-title"
            >
              {t('servicesOurServices') || 'Οι Υπηρεσίες μας'}
            </h2>
            <p className="text-purple-200/85 text-base md:text-lg">
              {t('servicesOurServicesSub') ||
                'Επαγγελματική τεχνογνωσία από έμπειρους συνεργάτες του ραδιοφωνικού χώρου'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {services.map((svc, idx) => {
              const Icon = SERVICE_ICON_MAP[svc.iconKey] || Settings2;
              return (
                <div
                  key={svc.id}
                  className="group relative flex flex-col rounded-2xl p-6 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-fuchsia-400/40 transition-all duration-300 shadow-lg hover:shadow-fuchsia-500/20"
                  data-testid={`service-card-${idx + 1}`}
                >
                  {/* Icon */}
                  <div
                    className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${svc.gradient} text-white mb-5 shadow-lg group-hover:scale-110 transition-transform duration-300`}
                  >
                    <Icon className="w-7 h-7" />
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-white mb-3 leading-tight min-h-[3.5rem]">
                    {svc.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm text-purple-100/75 mb-5 leading-relaxed">
                    {svc.desc}
                  </p>

                  {/* Bullet list label */}
                  <div className="text-xs font-semibold text-fuchsia-300 uppercase tracking-wider mb-3">
                    {t('svcIncludes') || 'Περιλαμβάνει:'}
                  </div>

                  {/* Bullets */}
                  <ul className="space-y-2 mb-6 flex-grow">
                    {svcBullets[svc.id].map((b, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-sm text-purple-100/85"
                      >
                        <CheckCircle2 className="w-4 h-4 text-fuchsia-400 mt-0.5 flex-shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <Link
                    to="/contact"
                    onClick={() => window.scrollTo(0, 0)}
                    className="block"
                    data-testid={`service-card-${idx + 1}-cta`}
                  >
                    <Button
                      className={`w-full bg-gradient-to-r ${svc.gradient} hover:opacity-90 text-white font-semibold`}
                    >
                      {t('svcLearnMore') || 'Μάθετε Περισσότερα'}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─────────── OUR PROCESS ─────────── */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14 max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold mb-3" data-testid="services-process-title">
              {t('servicesOurProcess') || 'Η Διαδικασία μας'}
            </h2>
            <p className="text-purple-200/85 text-base md:text-lg">
              {t('servicesOurProcessSub') ||
                'Πώς προσφέρουμε τις υπηρεσίες μας για να ενισχύσουμε τον ήχο του σταθμού σας'}
            </p>
          </div>

          <div className="relative max-w-6xl mx-auto">
            {/* horizontal dotted line connecting circles, hidden on mobile */}
            <div className="hidden lg:block absolute top-9 left-[12%] right-[12%] h-px border-t border-dashed border-fuchsia-400/30 pointer-events-none" />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6 relative">
              {processSteps.map((step, idx) => {
                const Icon = PROCESS_ICON_MAP[step.iconKey] || Sparkles;
                return (
                  <div
                    key={idx}
                    className="text-center"
                    data-testid={`process-step-${idx + 1}`}
                  >
                    <div className="relative inline-flex items-center justify-center w-18 h-18 mx-auto mb-5">
                      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-fuchsia-500/20 to-purple-600/20 blur-md" />
                      <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-fuchsia-500/30 to-purple-600/30 border border-fuchsia-300/40 flex items-center justify-center backdrop-blur-sm">
                        <Icon className="w-7 h-7 text-fuchsia-200" />
                      </div>
                      {/* step number */}
                      <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-fuchsia-500 text-white text-xs font-bold flex items-center justify-center shadow-md">
                        {idx + 1}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                    <p className="text-sm text-purple-100/75 leading-relaxed px-2">
                      {step.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── BENEFITS ─────────── */}
      <section className="py-16 lg:py-20 bg-black/15">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2
                className="text-3xl lg:text-4xl font-bold mb-3"
                data-testid="services-benefits-title"
              >
                {t('svcBenefitsTitle') || 'Τα οφέλη για τον σταθμό σας'}
              </h2>
              <p className="text-purple-200/85 text-base md:text-lg max-w-2xl mx-auto">
                {t('svcBenefitsSub') ||
                  'Συγκεκριμένα αποτελέσματα που θα δείτε μετά τη συνεργασία μαζί μας'}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
              {benefits.map((b, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-fuchsia-400/30 transition-all"
                  data-testid={`benefit-item-${i + 1}`}
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-fuchsia-500/30 to-purple-600/30 border border-fuchsia-300/30 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-fuchsia-300" />
                  </div>
                  <p className="text-sm md:text-base text-purple-50 leading-snug pt-1.5">
                    {b}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─────────── RESULTS / CASE STUDY (animated counters) ─────────── */}
      <section
        className="py-16 lg:py-20 relative overflow-hidden"
        data-testid="services-results-section"
      >
        {/* Subtle background flourish */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-1/4 w-72 h-72 bg-fuchsia-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-1/4 w-72 h-72 bg-amber-400/10 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 relative">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12 max-w-3xl mx-auto">
              <span
                className="inline-block px-3 py-1 mb-4 text-xs font-semibold uppercase tracking-wider rounded-full bg-amber-400/15 text-amber-200 border border-amber-300/30"
                data-testid="services-results-eyebrow"
              >
                {t('svcResultsEyebrow') || 'Αποτελέσματα · Case Studies'}
              </span>
              <h2
                className="text-3xl lg:text-4xl font-bold mb-3"
                data-testid="services-results-title"
              >
                {t('svcResultsTitle') ||
                  'Μετρήσιμα αποτελέσματα για τους σταθμούς-πελάτες μας'}
              </h2>
              <p className="text-purple-200/85 text-base md:text-lg">
                {t('svcResultsSub') ||
                  'Μέσοι όροι βελτίωσης μετά από 3-6 μήνες συνεργασίας με την ομάδα μας'}
              </p>
            </div>

            {/* 3 metric counters */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-12 max-w-4xl mx-auto">
              {[
                {
                  icon: Clock3,
                  value: 15,
                  prefix: '+',
                  suffix: '%',
                  label: t('svcResultMetric1') || 'Αύξηση TSL',
                  gradient: 'from-fuchsia-500 to-purple-600',
                  testId: 'result-metric-1',
                },
                {
                  icon: TrendingUp,
                  value: 5,
                  rangeTo: 10,
                  prefix: '+',
                  suffix: '%',
                  label: t('svcResultMetric2') || 'Αύξηση ακροαματικότητας',
                  gradient: 'from-blue-500 to-indigo-600',
                  testId: 'result-metric-2',
                },
                {
                  icon: TrendingDown,
                  value: 18,
                  prefix: '-',
                  suffix: '%',
                  label: t('svcResultMetric3') || 'Μείωση early drop',
                  gradient: 'from-amber-500 to-orange-600',
                  testId: 'result-metric-3',
                },
              ].map((m, i) => {
                const MIcon = m.icon;
                return (
                  <div
                    key={i}
                    className="relative rounded-2xl p-6 bg-white/5 border border-white/10 hover:bg-white/10 hover:border-fuchsia-400/40 transition-all duration-300 text-center"
                    data-testid={`${m.testId}-card`}
                  >
                    <div
                      className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${m.gradient} text-white mb-4 shadow-lg`}
                    >
                      <MIcon className="w-6 h-6" />
                    </div>
                    <div
                      className="text-4xl md:text-5xl font-extrabold leading-none mb-2 bg-gradient-to-r from-amber-300 via-fuchsia-300 to-purple-300 bg-clip-text text-transparent tabular-nums"
                      data-testid={m.testId}
                    >
                      <AnimatedCounter
                        to={m.value}
                        rangeTo={m.rangeTo ?? null}
                        prefix={m.prefix}
                        suffix={m.suffix}
                        duration={2500}
                        testId={`${m.testId}-counter`}
                      />
                    </div>
                    <p className="text-sm text-purple-100/85 leading-snug">
                      {m.label}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Disclaimer */}
            <p
              className="text-xs text-purple-300/60 text-center max-w-3xl mx-auto leading-relaxed"
              data-testid="services-results-disclaimer"
            >
              {t('svcResultsDisclaimer') ||
                'Αποτελέσματα βασισμένα σε αναλύσεις δεδομένων ακρόασης σταθμών που χρησιμοποιούν τις υπηρεσίες μας.'}
            </p>
          </div>
        </div>
      </section>

      {/* ─────────── FINAL CTA ─────────── */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-[#4A1D7A] via-[#5B2090] to-[#2D0C4B]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 rounded-full bg-white/10 border border-white/15 backdrop-blur-sm">
              <Users className="w-4 h-4 text-fuchsia-300" />
              <span className="text-sm text-purple-100">
                {t('svcCtaEyebrow') || 'Έτοιμοι να βελτιώσετε τον σταθμό σας;'}
              </span>
            </div>
            <h2
              className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-5 leading-tight"
              data-testid="services-final-cta-title"
            >
              {t('svcCtaTitle') || 'Θέλετε να βελτιώσετε το πρόγραμμα του σταθμού σας;'}
            </h2>
            <p className="text-base md:text-lg text-purple-100/90 mb-10 max-w-2xl mx-auto leading-relaxed">
              {t('svcCtaDesc') ||
                'Συζητήστε μαζί μας και θα αξιολογήσουμε δωρεάν το format, τη μουσική στρατηγική και τις δυνατότητες ανάπτυξης του σταθμού σας.'}
            </p>
            <Link to="/contact" onClick={() => window.scrollTo(0, 0)}>
              <Button
                size="lg"
                className="bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-[#2D0C4B] font-bold px-10 py-6 text-lg shadow-xl hover:shadow-amber-500/40 hover:scale-105 transition-all duration-300"
                data-testid="services-final-cta-button"
              >
                {t('servicesCtaFreeEval') || 'Ζητήστε Δωρεάν Αξιολόγηση'}
                <ArrowRight className="ml-2 w-6 h-6" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
